import { Link, useNavigate } from 'react-router-dom';
import { TableAction, TableColumn, TVGuide } from '@/types';
import { Edit, Eye, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useConfirmDialog, useTranslation } from '@/hooks';
import { useTVGuides, useDeleteTVGuide, useToggleTVGuideStatus, useSyncTVGuide } from '@/hooks';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import Toggle from '@/components/ui/Toggle';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout.tsx';

const TVGuides = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { confirm, dialog } = useConfirmDialog();
  const deleteTVGuideMutation = useDeleteTVGuide();
  const toggleStatusMutation = useToggleTVGuideStatus();
  const syncTVGuideMutation = useSyncTVGuide();

  const tvGuideColumns: TableColumn<TVGuide>[] = [
    {
      key: 'title',
      header: t.tvGuide.fields.title,
      accessor: (tvGuide) => (
        <div className="font-medium text-gray-900">
          <Link to={`/content/tv-channels/tv-guide/${tvGuide.id}/edit`} className="link">
            {tvGuide.title}
          </Link>
        </div>
      ),
      className: 'min-w-64'
    },
    {
      key: 'lastSyncDate',
      header: t.tvGuide.fields.lastSyncDate,
      accessor: (tvGuide) => (
        <span className="text-gray-700">
          {tvGuide.lastSyncDate ? new Date(tvGuide.lastSyncDate).toLocaleString() : '—'}
        </span>
      )
    },
    {
      key: 'lastSyncStatus',
      header: t.tvGuide.fields.lastSyncStatus,
      accessor: (tvGuide) => {
        const status = tvGuide.lastSyncStatus || 'NEVER_SYNCED';
        const colorMap: Record<string, string> = {
          SUCCESS: 'bg-green-100 text-green-800',
          FAILURE: 'bg-red-100 text-red-800',
          IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
          NEVER_SYNCED: 'bg-gray-100 text-gray-800',
        };
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorMap[status] || 'bg-gray-100 text-gray-800'}`}>
            {status.replace('_', ' ')}
          </span>
        );
      }
    },
    {
      key: 'syncErrorMessage',
      header: t.tvGuide.fields.syncErrorMessage,
      accessor: (tvGuide) => (
        <span className="text-gray-700" title={tvGuide.syncErrorMessage || ''}>
          {tvGuide.syncErrorMessage ? (tvGuide.syncErrorMessage.length > 60 ? `${tvGuide.syncErrorMessage.slice(0, 60)}…` : tvGuide.syncErrorMessage) : '—'}
        </span>
      ),
      className: 'max-w-xs truncate'
    },
    {
      key: 'active',
      header: t.tvGuide.fields.active,
      accessor: (tvGuide) => (
        <Toggle
          checked={tvGuide.active}
          onChange={(checked) => handleToggleStatus(tvGuide.id, checked)}
          disabled={toggleStatusMutation.isPending}
          size="sm"
        />
      )
    }
  ];

  const tvGuideActions: TableAction<TVGuide>[] = [
    {
      label: t.tvGuide.actions.edit,
      icon: Edit,
      onClick: (tvGuide) => navigate(`/content/tv-channels/tv-guide/${tvGuide.id}/edit`),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: t.tvGuide.actions.sync,
      icon: RefreshCw,
      onClick: (tvGuide) => handleSyncTVGuide(tvGuide.id),
      className: 'text-gray-700 hover:text-blue-600'
    },
    {
      label: t.tvGuide.actions.activate,
      icon: Eye,
      onClick: (tvGuide) => handleToggleStatus(tvGuide.id, !tvGuide.active),
      className: 'text-gray-700 hover:text-yellow-600',
      show: (tvGuide) => !tvGuide.active
    },
    {
      label: t.tvGuide.actions.deactivate,
      icon: Eye,
      onClick: (tvGuide) => handleToggleStatus(tvGuide.id, !tvGuide.active),
      className: 'text-gray-700 hover:text-yellow-600',
      show: (tvGuide) => tvGuide.active
    },
    {
      label: t.tvGuide.actions.delete,
      icon: Trash2,
      onClick: (tvGuide) => handleDeleteTVGuide(tvGuide.id),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  const handleCreateTVGuide = () => {
    navigate('/content/tv-channels/tv-guide/create');
  };

  const handleDeleteTVGuide = async (id: string) => {
    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: t.tvGuide.messages.confirmDelete,
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteTVGuideMutation.mutateAsync(id);
        },
      });

      if (!confirmed) {
        return;
      }
      toast.success(t.tvGuide.messages.deleted);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const handleToggleStatus = async (id: string, active: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({ id, active });
      toast.success(t.tvGuide.messages.statusUpdated);
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const handleSyncTVGuide = async (id: string) => {
    try {
      await syncTVGuideMutation.mutateAsync(id);
      console.log('TV guide sync triggered');
    } catch (error) {
      console.error('Sync error:', error);
    }
  };

  return (
    <TVChannelsLayout>
      <div className="w-full">
        <DataTableWithQuery
          showTotalCount
          tableStyle="compact"
          columns={tvGuideColumns}
          actions={tvGuideActions}
          useQueryHook={useTVGuides}
          headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">{t.tvGuide.listTitle}</h3>
                    <span
                      className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  <Button onClick={handleCreateTVGuide} size="sm">
                    <Plus className="mr-2 h-4 w-4"/>
                    Nueva guía
                  </Button>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">{t.tvGuide.listTitle}</h3>
                <span
                  className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-auto">
                  <Button onClick={handleCreateTVGuide} size="sm">
                    <Plus className="mr-2 h-4 w-4"/>
                    Nueva guía
                  </Button>
                </div>
              </div>
            </div>
          )}
          onCreate={handleCreateTVGuide}
          searchPlaceholder={t.tvGuide.placeholders.search}
          title={t.tvGuide.listTitle}
          itemsPerPage={10}
          className="rounded-none shadow-none border border-gray-200"
        />
        {dialog}
      </div>
    </TVChannelsLayout>
  );
};

export default TVGuides;
